import { AlertColor } from "@mui/material";
import {
  ConfigureMotionPhase,
  ConfigureMotionState,
} from "./siteAboutInterfaces";

// Shape of one Django message the action POST answers with.
export interface ActionMessage {
  message: string;
  level_tag: string;
  level: number;
}

// What configure_dw_http_motion.py reports per camera (connector-tools).
export interface ConfigureMotionCameraResult {
  camera_id: number | string | null;
  camera_name: string | null;
  status: "configured" | "already_configured" | "failed";
  detail?: string;
  stale_rules?: string[];
}

export interface ConfigureMotionResult {
  cameras?: ConfigureMotionCameraResult[];
  error?: string | null;
}

// GET customer/{id}/action_status/?action=configure_motion
export interface ActionStatusResponse {
  id: number;
  action: string;
  status: "pending" | "success" | "partial" | "failed" | null;
  result: ConfigureMotionResult | null;
  request_date: string;
  completed_at: string | null;
}

export const CONFIGURE_MOTION_POLL_INTERVAL_MS = 3000;
export const CONFIGURE_MOTION_POLL_ATTEMPTS = 40; // ~2 minutes
// Admin stamps request_date server-side; allow this much browser clock skew
// when deciding whether a row belongs to the click we just made.
export const DISPATCH_CLOCK_SKEW_MS = 60_000;

export const PENDING_MESSAGE =
  "Configuring motion. The Nx Witness rule is created by a background job — this usually takes under a minute.";
export const TIMEOUT_MESSAGE =
  "Configure Motion is still running. Check Event Rules in Nx Witness, or try again in a few minutes.";
export const REQUEST_FAILED_MESSAGE =
  "Configure Motion request failed. Please try again.";

export const IDLE_CONFIGURE_MOTION: ConfigureMotionState = {
  phase: "idle",
  message: "",
};

// The action POST answers with the backend's operator messages. A warning or
// error there means nothing was dispatched (e.g. the site has no legacy
// connector), so there will never be an outcome to poll for.
export const skippedMessage = (messages: unknown): string | null => {
  if (!Array.isArray(messages)) {
    return null;
  }
  const blocking = messages.find(
    (m: ActionMessage) =>
      m && (m.level_tag === "warning" || m.level_tag === "error")
  );
  return blocking ? String(blocking.message) : null;
};

const plural = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? "" : "s"}`;

export const summarizeConfigureMotion = (
  response: ActionStatusResponse
): ConfigureMotionState => {
  const cameras = response.result?.cameras ?? [];
  const configured = cameras.filter((c) => c.status === "configured");
  const already = cameras.filter((c) => c.status === "already_configured");
  const failed = cameras.filter((c) => c.status === "failed");
  const stale = cameras.filter(
    (c) => c.stale_rules && c.stale_rules.length > 0
  );

  const parts: string[] = [];
  if (configured.length) {
    parts.push(`rule created for ${plural(configured.length, "camera")}`);
  }
  if (already.length) {
    parts.push(`${already.length} already configured`);
  }
  const failedDetail = failed
    .map(
      (c) =>
        `${c.camera_name ?? c.camera_id}${c.detail ? ` (${c.detail})` : ""}`
    )
    .join("; ");

  switch (response.status) {
    case "success": {
      let message = `Motion configured: ${
        parts.join(", ") || "no changes needed"
      }.`;
      if (stale.length) {
        message += ` ${plural(stale.length, "camera")} ${
          stale.length === 1 ? "has" : "have"
        } an older Actuate rule pointing at a different camera — review it in Nx Witness.`;
      }
      return { phase: "success", message };
    }
    case "partial":
      return {
        phase: "partial",
        message: `Motion configured for some cameras (${parts.join(
          ", "
        )}) but failed for: ${failedDetail}.`,
      };
    case "failed": {
      const reason = response.result?.error || failedDetail || "unknown error";
      return { phase: "failed", message: `Configure Motion failed: ${reason}` };
    }
    default:
      return { phase: "pending", message: PENDING_MESSAGE };
  }
};

export const configureMotionSeverity = (
  phase: ConfigureMotionPhase
): AlertColor => {
  switch (phase) {
    case "success":
      return "success";
    case "failed":
      return "error";
    case "pending":
      return "info";
    default:
      // partial, skipped, timeout: something needs a look, nothing is broken
      // for certain.
      return "warning";
  }
};
